<template>
  <div class="paycheck-view-container">
    <MainHeader :title="'Paycheck Estimator'">
      <template #left>
        <font-awesome-icon
          @click="router.back()"
          :icon="['fas', 'arrow-left-long']"
          class="back-arrow pointer"
          role="button"
          aria-label="Go back"
        />
      </template>
    </MainHeader>

    <div class="view content content-padding">
      <!-- Intro -->
      <div class="intro-card">
        <div class="intro-icon">
          <font-awesome-icon :icon="['fas', 'money-check-dollar']" />
        </div>
        <p class="intro-text">
          Enter your gross pay, deductions, and see exactly what lands in your
          account each pay period.
        </p>
      </div>

      <!-- Pay Settings -->
      <div class="ud-section">
        <span class="section-label">Pay Details</span>

        <div class="wrapper-card">
          <div class="ud-field">
            <label class="ud-label">Gross pay</label>
            <div class="currency-input">
              <span class="currency-symbol">$</span>
              <input
                v-model.number="grossPay"
                type="number"
                class="input currency-field"
                placeholder="0"
                inputmode="decimal"
                min="0"
              />
            </div>
          </div>

          <div class="form-row mt-1">
            <div class="ud-field flex-1">
              <label class="ud-label">Pay frequency</label>
              <button
                type="button"
                class="form-select grey-outline-form-select category-trigger"
                :class="{ open: frequencyDropdownOpen }"
                @click="frequencyDropdownOpen = !frequencyDropdownOpen"
              >
                <span class="category-trigger-value">
                  {{ frequencyLabel }}
                </span>
                <font-awesome-icon
                  :icon="['fas', 'chevron-down']"
                  class="category-chevron"
                  :class="{ open: frequencyDropdownOpen }"
                />
              </button>
              <transition name="sub-slide">
                <div v-if="frequencyDropdownOpen" class="category-menu">
                  <button
                    v-for="freq in frequencies"
                    :key="freq.id"
                    type="button"
                    class="category-option"
                    :class="{ selected: payFrequency === freq.id }"
                    @click="selectFrequency(freq.id)"
                  >
                    <span class="option-name">{{ freq.label }}</span>
                  </button>
                </div>
              </transition>
            </div>

            <div class="ud-field flex-1">
              <label class="ud-label">Filing status</label>
              <button
                type="button"
                class="form-select grey-outline-form-select category-trigger"
                :class="{ open: filingDropdownOpen }"
                @click="filingDropdownOpen = !filingDropdownOpen"
              >
                <span class="category-trigger-value">
                  {{ filingLabel }}
                </span>
                <font-awesome-icon
                  :icon="['fas', 'chevron-down']"
                  class="category-chevron"
                  :class="{ open: filingDropdownOpen }"
                />
              </button>
              <transition name="sub-slide">
                <div v-if="filingDropdownOpen" class="category-menu">
                  <button
                    v-for="status in filingStatuses"
                    :key="status.id"
                    type="button"
                    class="category-option"
                    :class="{ selected: filingStatus === status.id }"
                    @click="selectFiling(status.id)"
                  >
                    <span class="option-name">{{ status.label }}</span>
                  </button>
                </div>
              </transition>
            </div>
          </div>

          <div class="form-row mt-1">
            <div class="ud-field flex-1">
              <label class="ud-label">State</label>
              <button
                type="button"
                class="form-select grey-outline-form-select category-trigger"
                :class="{ open: stateDropdownOpen }"
                @click="stateDropdownOpen = !stateDropdownOpen"
              >
                <span class="category-trigger-value">
                  {{ stateLabel }}
                </span>
                <font-awesome-icon
                  :icon="['fas', 'chevron-down']"
                  class="category-chevron"
                  :class="{ open: stateDropdownOpen }"
                />
              </button>
              <transition name="sub-slide">
                <div v-if="stateDropdownOpen" class="category-menu">
                  <button
                    v-for="st in states"
                    :key="st.id"
                    type="button"
                    class="category-option"
                    :class="{ selected: selectedState === st.id }"
                    @click="selectState(st.id)"
                  >
                    <span class="option-name">{{ st.label }}</span>
                  </button>
                </div>
              </transition>
            </div>

            <div class="ud-field flex-1">
              <label class="ud-label">Year</label>
              <button
                type="button"
                class="form-select grey-outline-form-select category-trigger"
                :class="{ open: yearDropdownOpen }"
                @click="yearDropdownOpen = !yearDropdownOpen"
              >
                <span class="category-trigger-value">
                  {{ taxYear }}
                </span>
                <font-awesome-icon
                  :icon="['fas', 'chevron-down']"
                  class="category-chevron"
                  :class="{ open: yearDropdownOpen }"
                />
              </button>
              <transition name="sub-slide">
                <div v-if="yearDropdownOpen" class="category-menu">
                  <button
                    v-for="yr in taxYears"
                    :key="yr"
                    type="button"
                    class="category-option"
                    :class="{ selected: taxYear === yr }"
                    @click="selectYear(yr)"
                  >
                    <span class="option-name">{{ yr }}</span>
                  </button>
                </div>
              </transition>
            </div>
          </div>
        </div>
      </div>

      <!-- Deductions -->
      <div class="ud-section">
        <div class="section-header-row">
          <span class="section-label">Deductions</span>
          <button
            v-if="deductions.length > 0 && !addingDeduction"
            type="button"
            class="link-btn"
            @click="startAddDeduction"
          >
            + Add another
          </button>
        </div>

        <div
          v-if="deductions.length === 0 && !addingDeduction"
          class="wrapper-card empty-debts"
        >
          <font-awesome-icon
            :icon="['fas', 'hand-holding-dollar']"
            class="empty-icon"
          />
          <p class="empty-title">No deductions added</p>
          <p class="empty-sub">
            Add 401(k), health insurance, HSA, or any other pre/post-tax
            deduction.
          </p>
          <button
            type="button"
            class="gradient-button"
            @click="startAddDeduction"
          >
            + Add your first deduction
          </button>
        </div>

        <div
          v-for="(ded, idx) in deductions"
          :key="ded.id"
          class="wrapper-card debt-card"
        >
          <div class="debt-card-header">
            <div class="debt-icon-wrap" :style="iconStyle(ded.color)">
              <font-awesome-icon
                :icon="['fas', ded.icon || 'hand-holding-dollar']"
              />
            </div>
            <div class="debt-info">
              <p class="debt-name">{{ ded.name }}</p>
              <p class="debt-meta">
                {{
                  ded.type === 'percentage'
                    ? ded.value + '%'
                    : '$' + formatNumber(ded.value)
                }}
                · {{ ded.taxTreatment }}
              </p>
            </div>
            <div class="debt-actions">
              <button
                type="button"
                class="icon-btn"
                @click="editDeduction(idx)"
                aria-label="Edit"
              >
                <font-awesome-icon :icon="['fas', 'pen']" />
              </button>
              <button
                type="button"
                class="icon-btn icon-btn-danger"
                @click="removeDeduction(idx)"
                aria-label="Remove"
              >
                <font-awesome-icon :icon="['fas', 'trash']" />
              </button>
            </div>
          </div>
          <div class="debt-balance-row">
            <span class="debt-balance-label">Per-paycheck impact</span>
            <span
              class="debt-balance-value"
              :class="{ 'text-green': ded.taxTreatment === 'pre-tax' }"
            >
              -${{ formatNumber(deductionImpact(ded)) }}
              <span v-if="ded.taxTreatment === 'pre-tax'" class="tax-saved-tag"
                >saves taxes</span
              >
            </span>
          </div>
        </div>

        <!-- Add/Edit Deduction Form -->
        <div v-if="addingDeduction" class="wrapper-card add-debt-card">
          <div class="add-debt-header">
            <span class="add-debt-title">
              {{
                editingDeductionIndex !== null
                  ? 'Edit Deduction'
                  : 'Add Deduction'
              }}
            </span>
            <button
              type="button"
              class="icon-btn"
              @click="cancelAddDeduction"
              aria-label="Cancel"
            >
              <font-awesome-icon :icon="['fas', 'xmark']" />
            </button>
          </div>

          <div class="ud-field mt">
            <label class="ud-label">Deduction name</label>
            <input
              v-model="dedForm.name"
              type="text"
              class="input"
              placeholder="e.g. 401(k), Health Insurance, HSA"
            />
          </div>

          <div class="form-row mt-1">
            <div class="ud-field flex-1">
              <label class="ud-label">Type</label>
              <div class="mode-toggle small">
                <button
                  type="button"
                  class="mode-btn"
                  :class="{ active: dedForm.type === 'fixed' }"
                  @click="dedForm.type = 'fixed'"
                >
                  Fixed $
                </button>
                <button
                  type="button"
                  class="mode-btn"
                  :class="{ active: dedForm.type === 'percentage' }"
                  @click="dedForm.type = 'percentage'"
                >
                  Percentage %
                </button>
              </div>
            </div>

            <div class="ud-field flex-1">
              <label class="ud-label">{{
                dedForm.type === 'percentage' ? 'Percentage' : 'Amount'
              }}</label>
              <div
                :class="
                  dedForm.type === 'percentage'
                    ? 'percent-input'
                    : 'currency-input'
                "
              >
                <span v-if="dedForm.type === 'fixed'" class="currency-symbol"
                  >$</span
                >
                <input
                  v-model.number="dedForm.value"
                  type="number"
                  class="input"
                  :class="
                    dedForm.type === 'percentage'
                      ? 'percent-field'
                      : 'currency-field'
                  "
                  placeholder="0"
                  inputmode="decimal"
                  min="0"
                  :step="dedForm.type === 'percentage' ? 0.01 : 1"
                />
                <span
                  v-if="dedForm.type === 'percentage'"
                  class="percent-symbol"
                  >%</span
                >
              </div>
            </div>
          </div>

          <div class="ud-field mt">
            <label class="ud-label">Tax treatment</label>
            <div class="mode-toggle small">
              <button
                type="button"
                class="mode-btn"
                :class="{ active: dedForm.taxTreatment === 'pre-tax' }"
                @click="dedForm.taxTreatment = 'pre-tax'"
              >
                Pre-tax
              </button>
              <button
                type="button"
                class="mode-btn"
                :class="{ active: dedForm.taxTreatment === 'post-tax' }"
                @click="dedForm.taxTreatment = 'post-tax'"
              >
                Post-tax
              </button>
            </div>
            <p class="deduction-hint">
              <font-awesome-icon :icon="['fas', 'circle-info']" />
              Pre-tax deductions reduce your taxable income (e.g. 401(k), HSA).
              Post-tax are taken after taxes (e.g. Roth 401(k), gym).
            </p>
          </div>

          <div v-if="dedFormError" class="form-error">
            <font-awesome-icon :icon="['fas', 'circle-exclamation']" />
            <span>{{ dedFormError }}</span>
          </div>

          <button
            type="button"
            class="gradient-button w-100 mt"
            :disabled="!isDedFormValid"
            @click="saveDeduction"
          >
            {{
              editingDeductionIndex !== null ? 'Save Changes' : 'Add Deduction'
            }}
          </button>
        </div>
      </div>

      <!-- Results -->
      <div v-if="grossPay > 0" class="ud-section">
        <span class="section-label">Your Paycheck</span>

        <div class="results-card green-gradient">
          <div class="result-stat-grid">
            <div class="result-stat">
              <span class="result-stat-label">Net take-home</span>
              <span class="result-stat-value net-value">
                ${{ formatNumber(netPay) }}
              </span>
              <span class="result-stat-sub">
                per {{ frequencyLabel.toLowerCase() }}
              </span>
            </div>
            <div class="result-stat">
              <span class="result-stat-label">Effective tax rate</span>
              <span class="result-stat-value">
                {{ effectiveTaxRate.toFixed(1) }}%
              </span>
              <span class="result-stat-sub">
                ${{ formatNumber(totalTax) }} total tax
              </span>
            </div>
            <div class="result-stat">
              <span class="result-stat-label">Annual estimate</span>
              <span class="result-stat-value">
                ${{ formatNumber(annualNet) }}
              </span>
              <span class="result-stat-sub">
                {{ periodsPerYear }} pay periods
              </span>
            </div>
          </div>
        </div>

        <!-- Breakdown -->
        <div class="wrapper-card mt milestones-card">
          <button
            type="button"
            class="expandable-header"
            @click="showBreakdown = !showBreakdown"
          >
            <span class="expandable-title">Paycheck breakdown</span>
            <font-awesome-icon
              :icon="['fas', 'chevron-down']"
              class="expandable-chevron"
              :class="{ open: showBreakdown }"
            />
          </button>
          <transition name="sub-slide">
            <div v-if="showBreakdown" class="milestones-body">
              <div class="breakdown-row">
                <span class="breakdown-label">Gross pay</span>
                <span class="breakdown-value gross"
                  >${{ formatNumber(grossPay) }}</span
                >
              </div>

              <div class="breakdown-divider" />

              <div
                v-for="ded in preTaxDeductions"
                :key="'pre-' + ded.id"
                class="breakdown-row deduction"
              >
                <span class="breakdown-label">
                  {{ ded.name }}
                  <span class="breakdown-tag pre-tax">pre-tax</span>
                </span>
                <span class="breakdown-value"
                  >-${{ formatNumber(deductionImpact(ded)) }}</span
                >
              </div>

              <div class="breakdown-row subtotal">
                <span class="breakdown-label">Taxable income</span>
                <span class="breakdown-value"
                  >${{ formatNumber(taxableIncome) }}</span
                >
              </div>

              <div class="breakdown-divider" />

              <div class="breakdown-row deduction">
                <span class="breakdown-label">Federal income tax</span>
                <span class="breakdown-value"
                  >-${{ formatNumber(federalTax) }}</span
                >
              </div>
              <div class="breakdown-row deduction">
                <span class="breakdown-label">Social Security (6.2%)</span>
                <span class="breakdown-value"
                  >-${{ formatNumber(socialSecurityTax) }}</span
                >
              </div>
              <div class="breakdown-row deduction">
                <span class="breakdown-label">Medicare (1.45%)</span>
                <span class="breakdown-value"
                  >-${{ formatNumber(medicareTax) }}</span
                >
              </div>
              <div v-if="stateTax > 0" class="breakdown-row deduction">
                <span class="breakdown-label">{{ stateLabel }} state tax</span>
                <span class="breakdown-value"
                  >-${{ formatNumber(stateTax) }}</span
                >
              </div>

              <div class="breakdown-divider" />

              <div
                v-for="ded in postTaxDeductions"
                :key="'post-' + ded.id"
                class="breakdown-row deduction"
              >
                <span class="breakdown-label">
                  {{ ded.name }}
                  <span class="breakdown-tag post-tax">post-tax</span>
                </span>
                <span class="breakdown-value"
                  >-${{ formatNumber(deductionImpact(ded)) }}</span
                >
              </div>

              <div class="breakdown-divider thick" />

              <div class="breakdown-row total">
                <span class="breakdown-label">Net pay</span>
                <span class="breakdown-value net"
                  >${{ formatNumber(netPay) }}</span
                >
              </div>
            </div>
          </transition>
        </div>

        <!-- Tax Bracket Info -->
        <div class="wrapper-card mt milestones-card">
          <button
            type="button"
            class="expandable-header"
            @click="showBrackets = !showBrackets"
          >
            <span class="expandable-title"
              >Federal tax brackets ({{ taxYear }})</span
            >
            <font-awesome-icon
              :icon="['fas', 'chevron-down']"
              class="expandable-chevron"
              :class="{ open: showBrackets }"
            />
          </button>
          <transition name="sub-slide">
            <div v-if="showBrackets" class="milestones-body">
              <div
                v-for="bracket in currentBrackets"
                :key="bracket.rate"
                class="bracket-row"
                :class="{ active: bracket.active }"
              >
                <div class="bracket-info">
                  <span class="bracket-rate"
                    >{{ (bracket.rate * 100).toFixed(0) }}%</span
                  >
                  <span class="bracket-range">
                    ${{ formatNumber(bracket.min) }} –
                    {{
                      bracket.max === Infinity
                        ? '∞'
                        : '$' + formatNumber(bracket.max)
                    }}
                  </span>
                </div>
                <span v-if="bracket.active" class="bracket-applied">
                  ${{ formatNumber(bracket.taxInBracket) }} taxed here
                </span>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import MainHeader from '../../components/View-Header.vue';

// 2024 Federal Tax Brackets
const TAX_BRACKETS = {
  2024: {
    single: [
      { rate: 0.1, min: 0, max: 11600 },
      { rate: 0.12, min: 11600, max: 47150 },
      { rate: 0.22, min: 47150, max: 100525 },
      { rate: 0.24, min: 100525, max: 191950 },
      { rate: 0.32, min: 191950, max: 243725 },
      { rate: 0.35, min: 243725, max: 609350 },
      { rate: 0.37, min: 609350, max: Infinity },
    ],
    married: [
      { rate: 0.1, min: 0, max: 23200 },
      { rate: 0.12, min: 23200, max: 94300 },
      { rate: 0.22, min: 94300, max: 201050 },
      { rate: 0.24, min: 201050, max: 383900 },
      { rate: 0.32, min: 383900, max: 487450 },
      { rate: 0.35, min: 487450, max: 731200 },
      { rate: 0.37, min: 731200, max: Infinity },
    ],
    hoh: [
      { rate: 0.1, min: 0, max: 16550 },
      { rate: 0.12, min: 16550, max: 63100 },
      { rate: 0.22, min: 63100, max: 100500 },
      { rate: 0.24, min: 100500, max: 191950 },
      { rate: 0.32, min: 191950, max: 243700 },
      { rate: 0.35, min: 243700, max: 609350 },
      { rate: 0.37, min: 609350, max: Infinity },
    ],
  },
  2025: {
    single: [
      { rate: 0.1, min: 0, max: 11925 },
      { rate: 0.12, min: 11925, max: 48475 },
      { rate: 0.22, min: 48475, max: 103350 },
      { rate: 0.24, min: 103350, max: 197300 },
      { rate: 0.32, min: 197300, max: 250525 },
      { rate: 0.35, min: 250525, max: 626350 },
      { rate: 0.37, min: 626350, max: Infinity },
    ],
    married: [
      { rate: 0.1, min: 0, max: 23850 },
      { rate: 0.12, min: 23850, max: 96950 },
      { rate: 0.22, min: 96950, max: 206700 },
      { rate: 0.24, min: 206700, max: 394600 },
      { rate: 0.32, min: 394600, max: 501050 },
      { rate: 0.35, min: 501050, max: 751600 },
      { rate: 0.37, min: 751600, max: Infinity },
    ],
    hoh: [
      { rate: 0.1, min: 0, max: 17000 },
      { rate: 0.12, min: 17000, max: 64850 },
      { rate: 0.22, min: 64850, max: 103350 },
      { rate: 0.24, min: 103350, max: 197300 },
      { rate: 0.32, min: 197300, max: 250500 },
      { rate: 0.35, min: 250500, max: 626350 },
      { rate: 0.37, min: 626350, max: Infinity },
    ],
  },
};

// Standard deductions
const STANDARD_DEDUCTION = {
  2024: { single: 14600, married: 29200, hoh: 21900 },
  2025: { single: 15000, married: 30000, hoh: 22500 },
};

// Simplified state tax rates (flat or approximate effective)
const STATE_TAX_RATES = {
  none: 0,
  ca: 0.093, // 9.3% effective
  ny: 0.0685, // 6.85%
  tx: 0, // no income tax
  fl: 0,
  wa: 0,
  nv: 0,
  tn: 0,
  sd: 0,
  wy: 0,
  ak: 0,
  il: 0.0495,
  pa: 0.0307,
  ma: 0.05,
  co: 0.044,
  az: 0.0259,
  or: 0.0875,
  nj: 0.0637,
  va: 0.0575,
  nc: 0.0475,
  ga: 0.0575,
  oh: 0.0399,
  mi: 0.0405,
  id: 0.058,
};

export default {
  components: { MainHeader },
  setup() {
    const router = useRouter();

    // Core inputs
    const grossPay = ref(0);
    const payFrequency = ref('biweekly');
    const filingStatus = ref('single');
    const selectedState = ref('none');
    const taxYear = ref(2025);

    // Dropdown states
    const frequencyDropdownOpen = ref(false);
    const filingDropdownOpen = ref(false);
    const stateDropdownOpen = ref(false);
    const yearDropdownOpen = ref(false);

    // Deductions
    const deductions = ref([]);
    const nextDedId = ref(1);
    const addingDeduction = ref(false);
    const editingDeductionIndex = ref(null);
    const dedForm = ref({
      name: '',
      value: 0,
      type: 'fixed',
      taxTreatment: 'pre-tax',
      icon: 'hand-holding-dollar',
      color: '#10B981',
    });
    const dedFormError = ref('');

    // UI
    const showBreakdown = ref(false);
    const showBrackets = ref(false);

    // Options
    const frequencies = [
      { id: 'weekly', label: 'Weekly', periods: 52 },
      { id: 'biweekly', label: 'Bi-weekly', periods: 26 },
      { id: 'semimonthly', label: 'Semi-monthly', periods: 24 },
      { id: 'monthly', label: 'Monthly', periods: 12 },
      { id: 'quarterly', label: 'Quarterly', periods: 4 },
      { id: 'annual', label: 'Annual', periods: 1 },
    ];

    const filingStatuses = [
      { id: 'single', label: 'Single' },
      { id: 'married', label: 'Married filing jointly' },
      { id: 'hoh', label: 'Head of household' },
    ];

    const states = [
      { id: 'none', label: 'No state tax / Other' },
      { id: 'ca', label: 'California' },
      { id: 'ny', label: 'New York' },
      { id: 'tx', label: 'Texas (no tax)' },
      { id: 'fl', label: 'Florida (no tax)' },
      { id: 'wa', label: 'Washington (no tax)' },
      { id: 'nv', label: 'Nevada (no tax)' },
      { id: 'il', label: 'Illinois' },
      { id: 'pa', label: 'Pennsylvania' },
      { id: 'ma', label: 'Massachusetts' },
      { id: 'co', label: 'Colorado' },
      { id: 'az', label: 'Arizona' },
      { id: 'or', label: 'Oregon' },
      { id: 'nj', label: 'New Jersey' },
      { id: 'va', label: 'Virginia' },
      { id: 'nc', label: 'North Carolina' },
      { id: 'ga', label: 'Georgia' },
      { id: 'oh', label: 'Ohio' },
      { id: 'mi', label: 'Michigan' },
      { id: 'id', label: 'Idaho' },
    ];

    const taxYears = [2024, 2025];

    // Computed labels
    const frequencyLabel = computed(
      () =>
        frequencies.find((f) => f.id === payFrequency.value)?.label ||
        'Bi-weekly'
    );
    const filingLabel = computed(
      () =>
        filingStatuses.find((f) => f.id === filingStatus.value)?.label ||
        'Single'
    );
    const stateLabel = computed(
      () =>
        states.find((s) => s.id === selectedState.value)?.label ||
        'No state tax'
    );

    const periodsPerYear = computed(
      () => frequencies.find((f) => f.id === payFrequency.value)?.periods || 26
    );

    const annualGross = computed(() => grossPay.value * periodsPerYear.value);

    // Deduction calculations
    const preTaxDeductions = computed(() =>
      deductions.value.filter((d) => d.taxTreatment === 'pre-tax')
    );
    const postTaxDeductions = computed(() =>
      deductions.value.filter((d) => d.taxTreatment === 'post-tax')
    );

    const deductionImpact = (ded) => {
      if (ded.type === 'percentage') {
        return (grossPay.value * ded.value) / 100;
      }
      return ded.value;
    };

    const totalPreTaxDeductions = computed(() =>
      preTaxDeductions.value.reduce((sum, d) => sum + deductionImpact(d), 0)
    );

    const totalPostTaxDeductions = computed(() =>
      postTaxDeductions.value.reduce((sum, d) => sum + deductionImpact(d), 0)
    );

    const taxableIncome = computed(() => {
      const annualTaxable = Math.max(
        0,
        annualGross.value -
          totalPreTaxDeductions.value * periodsPerYear.value -
          STANDARD_DEDUCTION[taxYear.value][filingStatus.value]
      );
      return annualTaxable / periodsPerYear.value;
    });

    const annualTaxableIncome = computed(
      () => taxableIncome.value * periodsPerYear.value
    );

    // Tax calculations
    const calculateFederalTax = (annualTaxable) => {
      const brackets = TAX_BRACKETS[taxYear.value][filingStatus.value];
      let tax = 0;
      let remaining = annualTaxable;

      for (const bracket of brackets) {
        if (remaining <= 0) break;
        const taxableInBracket = Math.min(remaining, bracket.max - bracket.min);
        tax += taxableInBracket * bracket.rate;
        remaining -= taxableInBracket;
      }

      return tax;
    };

    const federalTaxAnnual = computed(() =>
      calculateFederalTax(annualTaxableIncome.value)
    );
    const federalTax = computed(
      () => federalTaxAnnual.value / periodsPerYear.value
    );

    const socialSecurityTax = computed(() => {
      const annualSS = Math.min(annualGross.value, 168600) * 0.062; // 2024 wage base
      return annualSS / periodsPerYear.value;
    });

    const medicareTax = computed(() => {
      const annualMedicare = annualGross.value * 0.0145;
      const additional =
        annualGross.value > 200000 ? (annualGross.value - 200000) * 0.009 : 0; // single threshold
      return (annualMedicare + additional) / periodsPerYear.value;
    });

    const stateTax = computed(() => {
      const rate = STATE_TAX_RATES[selectedState.value] || 0;
      return (annualGross.value * rate) / periodsPerYear.value;
    });

    const totalTax = computed(
      () =>
        federalTax.value +
        socialSecurityTax.value +
        medicareTax.value +
        stateTax.value
    );

    const netPay = computed(() =>
      Math.max(
        0,
        grossPay.value -
          totalPreTaxDeductions.value -
          totalTax.value -
          totalPostTaxDeductions.value
      )
    );

    const annualNet = computed(() => netPay.value * periodsPerYear.value);

    const effectiveTaxRate = computed(() =>
      grossPay.value > 0 ? (totalTax.value / grossPay.value) * 100 : 0
    );

    // Bracket visualization
    const currentBrackets = computed(() => {
      const brackets = TAX_BRACKETS[taxYear.value][filingStatus.value];
      return brackets.map((b) => {
        const taxableInBracket = Math.max(
          0,
          Math.min(annualTaxableIncome.value, b.max) - b.min
        );
        return {
          ...b,
          active: taxableInBracket > 0,
          taxInBracket: taxableInBracket * b.rate,
        };
      });
    });

    // Deduction form methods
    const startAddDeduction = () => {
      addingDeduction.value = true;
      editingDeductionIndex.value = null;
      dedForm.value = {
        name: '',
        value: 0,
        type: 'fixed',
        taxTreatment: 'pre-tax',
        icon: 'hand-holding-dollar',
        color: '#10B981',
      };
      dedFormError.value = '';
    };

    const cancelAddDeduction = () => {
      addingDeduction.value = false;
      editingDeductionIndex.value = null;
      dedFormError.value = '';
    };

    const editDeduction = (idx) => {
      const d = deductions.value[idx];
      addingDeduction.value = true;
      editingDeductionIndex.value = idx;
      dedForm.value = { ...d };
      dedFormError.value = '';
    };

    const removeDeduction = (idx) => {
      deductions.value.splice(idx, 1);
    };

    const isDedFormValid = computed(() => {
      const f = dedForm.value;
      return f.name.trim() !== '' && f.value > 0;
    });

    const validateDedForm = () => {
      const f = dedForm.value;
      if (!f.name.trim()) return 'Give this deduction a name.';
      if (!(f.value > 0)) return 'Amount must be greater than 0.';
      if (f.type === 'percentage' && f.value > 100)
        return 'Percentage cannot exceed 100%.';
      return '';
    };

    const saveDeduction = () => {
      const err = validateDedForm();
      if (err) {
        dedFormError.value = err;
        return;
      }
      const f = dedForm.value;
      if (editingDeductionIndex.value !== null) {
        deductions.value[editingDeductionIndex.value] = {
          ...deductions.value[editingDeductionIndex.value],
          ...f,
        };
      } else {
        deductions.value.push({ ...f, id: nextDedId.value++ });
      }
      addingDeduction.value = false;
      editingDeductionIndex.value = null;
      dedFormError.value = '';
    };

    // Dropdown handlers
    const selectFrequency = (id) => {
      payFrequency.value = id;
      frequencyDropdownOpen.value = false;
    };
    const selectFiling = (id) => {
      filingStatus.value = id;
      filingDropdownOpen.value = false;
    };
    const selectState = (id) => {
      selectedState.value = id;
      stateDropdownOpen.value = false;
    };
    const selectYear = (yr) => {
      taxYear.value = yr;
      yearDropdownOpen.value = false;
    };

    // Click outside
    const handleClickOutside = (e) => {
      if (
        !e.target.closest('.category-trigger') &&
        !e.target.closest('.category-menu')
      ) {
        frequencyDropdownOpen.value = false;
        filingDropdownOpen.value = false;
        stateDropdownOpen.value = false;
        yearDropdownOpen.value = false;
      }
    };

    onMounted(() => {
      document.addEventListener('click', handleClickOutside);
    });

    onBeforeUnmount(() => {
      document.removeEventListener('click', handleClickOutside);
    });

    const formatNumber = (n) => {
      if (n == null || isNaN(n)) return '0';
      return Number(n).toLocaleString('en-US', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 0,
      });
    };

    const iconStyle = (color) => ({
      backgroundColor: color ? `${color}20` : '#f4f4f6',
      color: color || '#888',
    });

    return {
      router,
      grossPay,
      payFrequency,
      filingStatus,
      selectedState,
      taxYear,
      frequencyDropdownOpen,
      filingDropdownOpen,
      stateDropdownOpen,
      yearDropdownOpen,
      frequencies,
      filingStatuses,
      states,
      taxYears,
      frequencyLabel,
      filingLabel,
      stateLabel,
      periodsPerYear,
      deductions,
      addingDeduction,
      editingDeductionIndex,
      dedForm,
      dedFormError,
      isDedFormValid,
      startAddDeduction,
      cancelAddDeduction,
      editDeduction,
      removeDeduction,
      saveDeduction,
      deductionImpact,
      preTaxDeductions,
      postTaxDeductions,
      taxableIncome,
      federalTax,
      socialSecurityTax,
      medicareTax,
      stateTax,
      totalTax,
      netPay,
      annualNet,
      effectiveTaxRate,
      currentBrackets,
      showBreakdown,
      showBrackets,
      selectFrequency,
      selectFiling,
      selectState,
      selectYear,
      formatNumber,
      iconStyle,
    };
  },
};
</script>

<style scoped>
.input {
  width: 100%;
}

.paycheck-view-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.back-arrow {
  font-size: 18px;
  color: var(--color-primary);
  cursor: pointer;
  transition: transform var(--transition-fast);
}
.back-arrow:hover {
  transform: translateX(-2px);
}
.dark-theme .back-arrow {
  color: var(--color-accent-teal);
}

.intro-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--green-50, #ecfdf5);
  border-radius: var(--radius-lg);
  padding: 0.85rem 1rem;
  margin-bottom: 1.25rem;
  border: 1px solid #d1fae5;
}
.dark-theme .intro-card {
  background: #0f2a1c;
  border-color: #1a4d33;
}
.intro-icon {
  width: 36px;
  height: 36px;
  border-radius: var(--radius-sm);
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-accent-green, #10b981);
  font-size: 14px;
  flex-shrink: 0;
}
.dark-theme .intro-icon {
  background: #1a4d33;
}
.intro-text {
  font-size: 0.8rem;
  color: #213547;
  margin: 0;
  line-height: 1.4;
}
.dark-theme .intro-text {
  color: #d4d2d2;
}

.section-header-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 0.5rem;
}
.link-btn {
  background: none;
  border: none;
  padding: 0;
  color: var(--color-accent-green, #10b981);
  font-family: 'Poppins', sans-serif;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
}
.link-btn:hover {
  color: var(--green-600, #059669);
}

.empty-debts {
  text-align: center;
  padding: 2rem 1.5rem !important;
}
.empty-icon {
  font-size: 36px;
  color: var(--color-accent-green, #10b981);
  margin-bottom: 1rem;
}
.empty-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--color-primary);
  margin: 0 0 0.4rem;
}
.dark-theme .empty-title {
  color: white;
}
.empty-sub {
  font-size: 0.78rem;
  color: #6b7280;
  margin: 0 0 1.25rem;
}
.dark-theme .empty-sub {
  color: #9aa6b8;
}

.debt-card {
  margin-bottom: 0.75rem;
}
.debt-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 0.85rem;
}
.debt-icon-wrap {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
}
.debt-info {
  flex: 1;
  min-width: 0;
}
.debt-name {
  font-size: 0.9rem;
  font-weight: 700;
  color: #213547;
  margin: 0 0 2px;
}
.dark-theme .debt-name {
  color: white;
}
.debt-meta {
  font-size: 0.72rem;
  color: #6b7280;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dark-theme .debt-meta {
  color: #9aa6b8;
}
.debt-actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.debt-balance-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding-top: 0.85rem;
  border-top: 1px solid #f0f0f0;
}
.dark-theme .debt-balance-row {
  border-top-color: #2a3a4e;
}
.debt-balance-label {
  font-size: 0.75rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
}
.dark-theme .debt-balance-label {
  color: #9aa6b8;
}
.debt-balance-value {
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--color-primary);
}
.dark-theme .debt-balance-value {
  color: white;
}
.text-green {
  color: var(--color-accent-green, #10b981);
}
.tax-saved-tag {
  font-size: 0.65rem;
  font-weight: 600;
  color: var(--color-accent-green, #10b981);
  background: var(--green-50, #ecfdf5);
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: 6px;
}
.dark-theme .tax-saved-tag {
  background: #0f2a1c;
}

.icon-btn {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: background var(--transition-fast);
  padding: 0;
}
.icon-btn:hover {
  background: #f4f4f6;
}
.dark-theme .icon-btn {
  color: #9aa6b8;
}
.dark-theme .icon-btn:hover {
  background: #1a2535;
}
.icon-btn-danger:hover {
  background: #fdeee7;
  color: #d85a30;
}
.dark-theme .icon-btn-danger:hover {
  background: #2e1d18;
}
.icon-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.add-debt-card {
  border: 2px solid var(--color-accent-green, #10b981) !important;
}
.add-debt-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.85rem;
}
.add-debt-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--color-primary);
}
.dark-theme .add-debt-title {
  color: white;
}

.mode-toggle {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  background: #f4f4f6;
  padding: 4px;
  border-radius: var(--radius-pill);
}
.mode-toggle.small {
  margin-bottom: 0;
}
.dark-theme .mode-toggle {
  background: #1a2535;
}
.mode-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0.5rem 0.75rem;
  background: transparent;
  border: none;
  border-radius: var(--radius-pill);
  font-family: 'Poppins', sans-serif;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
}
.mode-btn.active {
  background: white;
  color: var(--color-primary);
  box-shadow: var(--shadow-sm);
}
.dark-theme .mode-btn {
  color: #9aa6b8;
}
.dark-theme .mode-btn.active {
  background: #212e3e;
  color: white;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}
  @media (min-width: 480px) {
  .form-row {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }
}
.form-row > .ud-field {
  min-width: 0;
}
.form-row .category-trigger,
.form-row .input {
  width: 100%;
  min-width: 0;
}
.flex-1 {
  flex: 1;
}

.currency-input,
.percent-input {
  position: relative;
}
.currency-symbol {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1em;
  font-weight: 600;
  color: #6b7280;
  pointer-events: none;
  z-index: 1;
}
.percent-symbol {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1em;
  font-weight: 600;
  color: #6b7280;
  pointer-events: none;
  z-index: 1;
}
.dark-theme .currency-symbol,
.dark-theme .percent-symbol {
  color: #9aa6b8;
}
.currency-field {
  padding-left: 1.85rem !important;
}
.percent-field {
  padding-right: 1.85rem !important;
}

.form-error {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 0.65rem 0.85rem;
  border-radius: var(--radius-sm);
  font-size: 0.78rem;
  line-height: 1.4;
  margin-top: 0.85rem;
  background: #fdeee7;
  color: #b03d12;
  border: 1px solid #f5cdb8;
}
.dark-theme .form-error {
  background: #2e1d18;
  color: #f5b89a;
  border-color: #4a2a20;
}

.deduction-hint {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 0.6rem;
  padding: 0.6rem 0.75rem;
  background: var(--green-50, #ecfdf5);
  border-radius: var(--radius-sm);
  font-size: 0.72rem;
  color: #4b5563;
  line-height: 1.4;
}
.deduction-hint svg {
  color: var(--color-accent-green, #10b981);
  font-size: 11px;
  margin-top: 2px;
  flex-shrink: 0;
}
.dark-theme .deduction-hint {
  background: #0f2a1c;
  color: #cbd5e1;
}

.category-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  text-align: left;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  background-image: none !important;
  padding-right: 14px !important;
  font-family: 'Poppins', sans-serif;
  color: #213547;
}
.dark-theme .category-trigger {
  color: #d4d2d2;
}
.category-trigger.open {
  border-color: var(--color-accent-green, #10b981) !important;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}
.category-trigger:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.category-trigger-value {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.category-chevron {
  font-size: 11px;
  color: #6b7280;
  transition: transform var(--transition-fast);
  flex-shrink: 0;
}
.category-chevron.open {
  transform: rotate(180deg);
}

.category-menu {
  margin-top: 6px;
  background: white;
  border: 1.5px solid #e7e7e7;
  border-radius: var(--radius-sm);
  overflow: hidden;
  max-height: 320px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
.dark-theme .category-menu {
  background: #212e3e;
  border-color: #2a3a4e;
}
.category-option {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0.6rem 0.85rem;
  background: transparent;
  border: none;
  border-bottom: 1px solid #f4f4f6;
  border-radius: 0;
  font-family: 'Poppins', sans-serif;
  font-size: 0.85rem;
  color: #213547;
  text-align: left;
  cursor: pointer;
}
.category-option:last-child {
  border-bottom: none;
}
.category-option:hover {
  background: #fafafa;
}
.category-option.selected {
  background: var(--green-50, #ecfdf5);
  color: var(--color-accent-green, #10b981);
  font-weight: 600;
}
.dark-theme .category-option {
  color: #d4d2d2;
  border-bottom-color: #2a3a4e;
}
.dark-theme .category-option:hover {
  background: #1a2535;
}
.dark-theme .category-option.selected {
  background: #0f2a1c;
}
.option-name {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.results-card {
  background: linear-gradient(
    135deg,
    var(--green-50, #ecfdf5) 0%,
    var(--teal-50, #f0fdfa) 100%
  );
  border-radius: var(--radius-lg);
  padding: 1.25rem 1.15rem;
  border: 1px solid #d1fae5;
  animation: cardSlideUp 0.4s cubic-bezier(0.34, 1.2, 0.64, 1) both;
}
.dark-theme .results-card {
  background: linear-gradient(135deg, #0f2a1c 0%, #0d2420 100%);
  border-color: #1a4d33;
}
.green-gradient {
  background: linear-gradient(135deg, #ecfdf5 0%, #f0fdfa 100%);
}
.dark-theme .green-gradient {
  background: linear-gradient(135deg, #0f2a1c 0%, #0d2420 100%);
}
.result-stat-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.85rem;
}
@media (min-width: 480px) {
  .result-stat-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}
.result-stat {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.result-stat-label {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #6b7280;
}
.dark-theme .result-stat-label {
  color: #9aa6b8;
}
.result-stat-value {
  font-size: 1.25rem;
  font-weight: 800;
  color: var(--color-primary);
  letter-spacing: -0.02em;
}
.dark-theme .result-stat-value {
  color: white;
}
.net-value {
  color: var(--color-accent-green, #10b981);
}
.dark-theme .net-value {
  color: var(--color-accent-teal, #2dd4bf);
}
.result-stat-sub {
  font-size: 0.7rem;
  color: #6b7280;
}
.dark-theme .result-stat-sub {
  color: #9aa6b8;
}

.milestones-card {
  padding: 0 !important;
  overflow: hidden;
}
.expandable-header {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.15rem;
  background: transparent;
  border: none;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  text-align: left;
}
.expandable-title {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--color-primary);
}
.dark-theme .expandable-title {
  color: white;
}
.expandable-chevron {
  font-size: 12px;
  color: #6b7280;
  transition: transform var(--transition-fast);
}
.expandable-chevron.open {
  transform: rotate(180deg);
}

.milestones-body {
  padding: 0 1.15rem 1rem;
}

/* Breakdown styles */
.breakdown-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 0.55rem 0;
}
.breakdown-label {
  font-size: 0.82rem;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 6px;
}
.dark-theme .breakdown-label {
  color: #9aa6b8;
}
.breakdown-value {
  font-size: 0.85rem;
  font-weight: 600;
  color: #213547;
  flex-shrink: 0;
}
.dark-theme .breakdown-value {
  color: #d4d2d2;
}
.breakdown-value.gross {
  color: var(--color-primary);
  font-weight: 700;
}
.dark-theme .breakdown-value.gross {
  color: white;
}
.breakdown-value.net {
  color: var(--color-accent-green, #10b981);
  font-size: 1rem;
  font-weight: 800;
}
.dark-theme .breakdown-value.net {
  color: var(--color-accent-teal, #2dd4bf);
}
.breakdown-tag {
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 2px 5px;
  border-radius: 4px;
}
.breakdown-tag.pre-tax {
  background: var(--green-50, #ecfdf5);
  color: var(--color-accent-green, #10b981);
}
.dark-theme .breakdown-tag.pre-tax {
  background: #0f2a1c;
}
.breakdown-tag.post-tax {
  background: #f4f4f6;
  color: #6b7280;
}
.dark-theme .breakdown-tag.post-tax {
  background: #1a2535;
  color: #9aa6b8;
}
.breakdown-divider {
  height: 1px;
  background: #f0f0f0;
  margin: 0.3rem 0;
}
.dark-theme .breakdown-divider {
  background: #2a3a4e;
}
.breakdown-divider.thick {
  height: 2px;
  background: #e7e7e7;
}
.dark-theme .breakdown-divider.thick {
  background: #3a4a5e;
}
.breakdown-row.deduction .breakdown-label {
  padding-left: 1rem;
}
.breakdown-row.subtotal {
  padding: 0.7rem 0;
  border-top: 1px dashed #e7e7e7;
  border-bottom: 1px dashed #e7e7e7;
  margin: 0.2rem 0;
}
.dark-theme .breakdown-row.subtotal {
  border-color: #2a3a4e;
}
.breakdown-row.subtotal .breakdown-label,
.breakdown-row.subtotal .breakdown-value {
  font-weight: 700;
  color: var(--color-primary);
}
.dark-theme .breakdown-row.subtotal .breakdown-label,
.dark-theme .breakdown-row.subtotal .breakdown-value {
  color: white;
}
.breakdown-row.total {
  padding-top: 0.7rem;
}

/* Bracket styles */
.bracket-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 0.65rem 0;
  border-top: 1px solid #f4f4f6;
  opacity: 0.6;
}
.dark-theme .bracket-row {
  border-top-color: #2a3a4e;
}
.bracket-row.active {
  opacity: 1;
  background: var(--green-50, #ecfdf5);
  margin: 0 -1.15rem;
  padding-left: 1.15rem;
  padding-right: 1.15rem;
  border-top: none;
}
.dark-theme .bracket-row.active {
  background: #0f2a1c;
}
.bracket-info {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}
.bracket-rate {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--color-primary);
  min-width: 36px;
}
.dark-theme .bracket-rate {
  color: white;
}
.bracket-range {
  font-size: 0.72rem;
  color: #6b7280;
}
.dark-theme .bracket-range {
  color: #9aa6b8;
}
.bracket-applied {
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--color-accent-green, #10b981);
  flex-shrink: 0;
}
.dark-theme .bracket-applied {
  color: var(--color-accent-teal, #2dd4bf);
}
</style>
